import { masterMark } from '@deltasuite/ui-tokens';
import { useState } from 'react';
import { useAuth } from '@deltasuite/auth';

// Magic-link sign-in. Email input + one button. The link in the email
// brings the user back here with the session attached; AuthProvider picks
// it up via Supabase's detectSessionInUrl and flips state to signed_in.
export function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      await signIn({ kind: 'magic_link', email });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send sign-in link.');
    }
  };

  return (
    <main className="flex min-h-full items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 text-graphite-700">
          <span className="font-sans text-2xl leading-none text-signal-amber" aria-hidden>
            {masterMark}
          </span>
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            DeltaDial
          </span>
        </div>

        <h1 className="mt-10 font-sans text-3xl font-semibold tracking-tight text-graphite-900">
          Sign in.
        </h1>
        <p className="mt-2 font-sans text-sm text-graphite-700">
          We’ll email you a one-click sign-in link. Zanda email only.
        </p>

        {status === 'sent' ? (
          <div className="mt-8 rounded-md border border-graphite-100 bg-surface-sunk p-4">
            <p className="font-sans text-sm text-graphite-900">Check your inbox.</p>
            <p className="mt-1 font-mono text-xs text-graphite-500">
              The link expires in 60 minutes. You can close this tab.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-3">
            <label className="block">
              <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
                Email
              </span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@zanda.com.au"
                className="mt-1 w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900 focus:border-signal-amber focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={status === 'sending' || !email.trim()}
              className="w-full rounded-md bg-graphite-900 px-4 py-2.5 font-sans text-sm font-medium text-surface transition-colors hover:bg-graphite-700 disabled:opacity-40"
            >
              {status === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
            </button>
            {error ? (
              <p role="alert" className="font-mono text-xs text-signal-amber">
                {error}
              </p>
            ) : null}
          </form>
        )}
      </div>
    </main>
  );
}
